import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { useSMPData } from './hooks/useSMPData';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { SMPLandingPageSkeleton } from './SMPLandingPageSkeleton';
import { SMPLandingPageHeader } from './sections/SMPLandingPageHeader';
import { SMPLandingPageNextOrder } from './sections/SMPLandingPageNextOrder';
import { SMPLandingPageOrderInProgress } from './sections/SMPLandingPageOrderInProgress';
import { SMPLandingPageManageSection } from './sections/SMPLandingPageManageSection';
import { SMPLandingPageShippingPayment } from './sections/SMPLandingPageShippingPayment';
import { SMPLandingPageOrders } from './sections/SMPLandingPageOrders';
import { SMPLandingPageInfo } from './sections/SMPLandingPageInfo';
import { SMPMutationErrorDialog, SMPMaintenanceBanner } from './components';
import { NavBar } from '@design-system/NavBar.tsx';
import { ACCOUNT_MANAGEMENT_PATH, SMP_DETAIL_PATH } from '@/constants/paths';
import { isSubscriptionPaused, formatSMPDate, isKitSubscription, isComponentizationEligible } from './utils/smpHelpers';
import { useUIMessages } from './hooks/useUIMessages';
import { SkipOrderBlockDialog, type SkipBlockReason } from './components/SkipOrderBlockDialog';
import { LatestResultsBanner } from '@/components/shared/LatestResultsBanner';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';

const NOD_PENDING = 'Pending';

export const SMPLandingPage = () => {
  const navigate = useNavigate();
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const { smpNavigate, withSubId } = useSMPNavigate();
  const {
    subscriptionDetail,
    productComponents,
    customerDiscount,
    customerDiscounts,
    hasRecommendation,
    hasPersonalizedSupplementRecommendations,
    isEligibleForSupplements,
    hasOrderInProgress,
    canSkip,
    isOnHoldingPatternState,
    showMaintenance,
    uiMessages,
    isLoading,
    isError,
    hasNonCriticalError,
    isInvalidSubscription,
  } = useSMPData({ screenName: 'SMPLandingPage' });

  const { getMessageText } = useUIMessages(uiMessages);
  // SMP is embedded as an iframe in the mobile app; the context switcher lives
  // in the web shell, so the "Change context" banner is web-only.
  const isIframeMode = useIframeModeStore((s) => s.isIframeMode);

  const [showNonCriticalErrorDialog, setShowNonCriticalErrorDialog] = useState(false);
  const [showSkipBlockDialog, setShowSkipBlockDialog] = useState(false);
  const [skipBlockReason, setSkipBlockReason] = useState<SkipBlockReason>(null);

  useEffect(() => {
    if (hasNonCriticalError) {
      setShowNonCriticalErrorDialog(true);
    }
  }, [hasNonCriticalError]);

  const handleSkipOrderClick = () => {
    if (!subscriptionDetail) return;

    const isPaused = isSubscriptionPaused(subscriptionDetail.subscription_status);
    const isNODPending = subscriptionDetail.next_order_datetime === NOD_PENDING;

    if (isPaused) {
      setSkipBlockReason('paused');
      setShowSkipBlockDialog(true);
    } else if (isNODPending) {
      setSkipBlockReason('nod-pending');
      setShowSkipBlockDialog(true);
    } else if (!canSkip) {
      setSkipBlockReason('already-skipped');
      setShowSkipBlockDialog(true);
    } else if (isOnHoldingPatternState) {
      setSkipBlockReason('holding-pattern');
      setShowSkipBlockDialog(true);
    } else {
      smpNavigate('/smp/skip-next-order');
    }
  };

  if (isLoading) {
    return <SMPLandingPageSkeleton />;
  }

  // An invalid `?subscriptionId=` (not in the customer's list) → bounce back
  // to the account selector rather than mis-scoping the portal. A missing id
  // (deeplink) is resolved by useSMPData's derive fallback, not bounced.
  if (isInvalidSubscription) {
    return <Navigate to={ACCOUNT_MANAGEMENT_PATH} replace />;
  }

  if (isError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  // Safety net: a componentized subscription reached this route (stale link,
  // bookmark, direct nav) — bounce to the new detail page instead of
  // rendering the legacy flow for it. See
  // SMP_COMPONENTIZATION_TECH_SPEC_v1.md §7 Open Question 5.
  // Prefer the BE-driven `componentization` flag from the detail API; fall back
  // to the SKU allowlist. Also gated by the enable_componentization kill switch.
  if (
    isComponentizationEnabled &&
    isComponentizationEligible(
      subscriptionDetail.componentization,
      subscriptionDetail.line_items,
      eligibleSkus,
    )
  ) {
    return <Navigate to={withSubId(SMP_DETAIL_PATH)} replace />;
  }

  const { subscription_status } = subscriptionDetail;
  const isKit = isKitSubscription(subscriptionDetail.line_items);
  const blurredClass = showMaintenance ? 'blur-sm pointer-events-none select-none' : '';

  return (
    <section
      className="page-container flex flex-col gap-12 pb-24"
      data-testid="smp-landingPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          data-testid="navbar"
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={() => navigate(ACCOUNT_MANAGEMENT_PATH)}
        />

        {!isIframeMode && (
          <LatestResultsBanner message="Subscriptions based on latest test results." />
        )}

        {showMaintenance && <SMPMaintenanceBanner />}

        {/* Page Title */}
        <h1
          className="text-[32px] md:text-[40px] font-normal text-primary"
          data-testid="smp-pageTitle"
        >
          My Subscription
        </h1>

        {/* 1. Header Section – Plan details, status badge, product images */}
        <div className={blurredClass}>
          <SMPLandingPageHeader subscription={subscriptionDetail} productComponents={productComponents} customerDiscount={customerDiscount} customerDiscounts={customerDiscounts} />
        </div>

        {/* 2. Next Order Section – Date, info icon, "I need it sooner" */}
        <div className={blurredClass}>
          <SMPLandingPageNextOrder
            subscription={subscriptionDetail}
            hasRecommendation={hasRecommendation}
            hasOrderInProgress={hasOrderInProgress}
            uiMessages={uiMessages}
          />
        </div>

        {/* 3. Order in Progress Section – Status bar, order details link */}
        {hasOrderInProgress && (
          <div className={blurredClass}>
            <SMPLandingPageOrderInProgress />
          </div>
        )}
      </div>

      {/* 4. Manage Subscription Section — hidden during maintenance */}
      {!showMaintenance && (
        <SMPLandingPageManageSection
          subscriptionStatus={subscription_status}
          hasPersonalizedSupplementRecommendations={hasPersonalizedSupplementRecommendations}
          isEligibleForSupplements={isEligibleForSupplements}
          isKit={isKit}
          onSkipOrderClick={handleSkipOrderClick}
        />
      )}

      {/* 5. Shipping & Payment Section */}
      <div className={blurredClass}>
        <SMPLandingPageShippingPayment subscription={subscriptionDetail} />
      </div>

      {/* 6. Orders Section */}
      <div className={blurredClass}>
        <SMPLandingPageOrders subscriptionStatus={subscription_status} />
      </div>

      {/* 7. Information Section */}
      <SMPLandingPageInfo />

      <SkipOrderBlockDialog
        open={showSkipBlockDialog}
        reason={skipBlockReason}
        messageHtml={(() => {
          switch (skipBlockReason) {
            case 'paused': return getMessageText('smp_paused_msg', 'bodyHtml');
            case 'nod-pending': return getMessageText('smp_nod_pending_msg', 'bodyHtml');
            case 'already-skipped': return getMessageText('smp_already_skipped', 'bodyHtml', { skippedDate: formatSMPDate(subscriptionDetail.next_order_datetime) });
            case 'holding-pattern': return getMessageText('smp_holding_pattern_state_msg', 'bodyHtml');
            default: return '';
          }
        })()}
        ctaLabel={skipBlockReason === 'holding-pattern' ? getMessageText('smp_pause_subscription', 'headerText', undefined, 'Pause Subscription') : undefined}
        onCtaClick={skipBlockReason === 'holding-pattern' ? () => {
          setShowSkipBlockDialog(false);
          smpNavigate('/smp/pause-subscription');
        } : undefined}
        onClose={() => setShowSkipBlockDialog(false)}
      />

      <SMPMutationErrorDialog
        open={showNonCriticalErrorDialog}
        onClose={() => setShowNonCriticalErrorDialog(false)}
        description="Some subscription information could not be loaded. You may see incomplete data on this page."
      />
    </section>
  );
};
